import React, { useState, createContext, useContext } from 'react';
import { clsx } from 'clsx';

// Layout Context
interface LayoutContextType {
  sidebarCollapsed: boolean;
  setSidebarCollapsed: (collapsed: boolean) => void;
}

const LayoutContext = createContext<LayoutContextType | undefined>(undefined);

// Main Layout Component
interface LayoutProps {
  children: React.ReactNode;
  className?: string;
}

const Layout: React.FC<LayoutProps> = ({ children, className }) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  return (
    <LayoutContext.Provider value={{ sidebarCollapsed, setSidebarCollapsed }}>
      <div className={clsx('min-h-screen bg-gray-50', className)}>
        {children}
      </div>
    </LayoutContext.Provider>
  );
};

// Header Component
interface HeaderProps {
  children: React.ReactNode;
  className?: string;
}

const Header: React.FC<HeaderProps> = ({ children, className }) => {
  return (
    <header className={clsx(
      'bg-white shadow-sm border-b border-gray-200 px-6 py-4 flex items-center justify-between',
      className
    )}>
      {children}
    </header>
  );
};

// Sidebar Component
interface SidebarProps {
  children: React.ReactNode;
  className?: string;
  width?: string;
  collapsible?: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({ 
  children, 
  className, 
  width = '256px',
  collapsible = false 
}) => {
  const context = useContext(LayoutContext);
  const collapsed = collapsible && context?.sidebarCollapsed;
  
  return (
    <aside 
      className={clsx(
        'bg-white border-r border-gray-200 flex-shrink-0 transition-all duration-300',
        collapsed ? 'w-16' : `w-[${width}]`,
        className
      )}
      style={{ width: collapsed ? '64px' : width }}
    >
      <div className="h-full overflow-y-auto">
        {children}
      </div>
    </aside>
  );
};

// Content Component
interface ContentProps {
  children: React.ReactNode;
  className?: string;
  padding?: 'none' | 'sm' | 'md' | 'lg';
}

const Content: React.FC<ContentProps> = ({ children, className, padding = 'md' }) => {
  const paddingClasses = {
    none: '',
    sm: 'p-4',
    md: 'p-6',
    lg: 'p-8'
  };

  return (
    <main className={clsx(
      'flex-1 overflow-auto',
      paddingClasses[padding],
      className
    )}>
      {children}
    </main>
  );
};

// Hook to use layout context
export const useLayout = () => {
  const context = useContext(LayoutContext);
  if (context === undefined) {
    throw new Error('useLayout must be used within a Layout component');
  }
  return context;
};

// Export all components
export { Layout, Header, Sidebar, Content };
export default Layout; 